import { Chain } from '@1delta/chain-registry'
import { TokenListInput } from './fetchOraclePrices'
import {
  StructuredOraclePrices,
  USDPriceMap,
  FlattenPriorityConfig,
  OutlierGuardConfig,
  TvlMap,
  DepthMap,
  PriceDerivation,
  PriceSelection,
} from './types'
import { Lender } from '@1delta/lender-registry'
import { DERIVATION_RANK, resolveDerivation } from './derivation'
import { lenderFamily } from './lenderKeys'
import { DEFAULT_OUTLIER_GUARD, rejectOutliers } from './outlierGuard'
import {
  FeedStatsMap,
  FRESHNESS_RANK,
  RELIABILITY_RANK,
  classifyFreshness,
  classifyReliability,
  feedKeyOf,
  feedStatKey,
} from './feedStats'

// ---------------------------------------------------------------------------
// Price flattening: evidence first, configuration second, depth third
// ---------------------------------------------------------------------------

/**
 * Feeds older than this are demoted below every fresh candidate, when the
 * feed has no learned cadence of its own yet.
 *
 * 48h = 2x the slowest standard Chainlink heartbeat (24h). A healthy
 * stablecoin feed on a 24h heartbeat is routinely 10-20h old, so anything
 * tighter would demote perfectly good prices. Once `feedStats` has watched a
 * feed for a few updates, its own cadence replaces this constant.
 */
export const DEFAULT_STALE_REJECT_SECONDS = 172_800

/**
 * Prefix of the runtime-synthesized Aave V4 per-spoke lender keys
 * (`AAVE_V4_<SPOKE_HEX>`, see `aaveV4SpokeLenderKey`). There is no enum
 * member to reference — V4 spokes are not part of the `Lender` enum.
 */
const AAVE_V4_PREFIX = 'AAVE_V4'

/**
 * `assetGroup` strings that mean "resolution failed" rather than an identity.
 * A group keyed by one of these collides across unrelated tokens — every
 * unresolvable token collapses onto the same key (e.g. the literal
 * `"undefined"`, or `"0x::0x"`), so an entry carrying one is excluded
 * outright. Mirrors `isPlaceholderGroup` in the yield-tracer's
 * `placeholderAssets.ts`; the two must stay in sync.
 */
const PLACEHOLDER_GROUP_SEGMENTS: Record<string, true> = {
  undefined: true,
  '0x': true,
  unknown: true,
  'unknown token': true,
  unk: true,
  'n/a': true,
  na: true,
  none: true,
  '?': true,
  '-': true,
}

const isPlaceholderSegment = (segment: string): boolean =>
  PLACEHOLDER_GROUP_SEGMENTS[segment.trim().toLowerCase()] === true

/**
 * True when `group` is built from a placeholder segment — not an identity, so
 * the entry must not be keyed by it. This is the FLR `$29.49` collision: a
 * token whose group resolved to the literal `"undefined"` was published under
 * that key, and every native token with no `assetGroup` read it as its own
 * price via `prices[undefined]`.
 */
const isPlaceholderGroup = (group: string): boolean =>
  group.split('::').some(isPlaceholderSegment)

export { DEFAULT_OUTLIER_GUARD } from './outlierGuard'

const DEFAULT_PRIORITY: FlattenPriorityConfig = {
  chainPriority: [
    Chain.ETHEREUM_MAINNET,
    Chain.BNB_SMART_CHAIN_MAINNET,
    Chain.ARBITRUM_ONE,
    Chain.BASE,
    Chain.AVALANCHE_C_CHAIN,
  ],
  // Aave V4 has no enum member — every key is the runtime-synthesized
  // `AAVE_V4_<SPOKE_HEX>` — so it is matched by the literal prefix, the same
  // way `isAaveV4Type()` does.
  lenderPriority: [Lender.AAVE_V3, AAVE_V4_PREFIX, 'COMPOUND_V3'],
  lowPriorityLenders: [Lender.ZEROLEND, Lender.AVALON, Lender.MORPHO_BLUE],
  excludedLenders: {
    // paused
    '8217': ['KLAP'],
    // shutdown
    '100': ['AGAVE'],
    // invalid values
    '1': [Lender.ZEROLEND_STABLECOINS_RWA, Lender.ZEROLEND_ETH_LRTS],
    '534352': ['LORE'],
    '167000': [Lender.TAKOTAKO, Lender.TAKOTAKO_ETH],
    // GAMMA (Planet Finance) is abandoned and its oracle is frozen at ~2022
    // prices across the board — AVAX $23.78 vs $6.44, BTCB $23k vs $64k, and
    // it still quotes LUNA/UST. Kept as a hard veto because it prices several
    // dead assets NOTHING else prices, so no consensus can form to catch it.
    '56': [Lender.AVALON_USDX, Lender.GAMMA],
    // broken
    '169': [Lender.ZEROLEND],
  },
  lenderPriorityPerChain: {
    // NOTE: a per-chain list REPLACES the global one, so it has to repeat
    // every prefix that should still rank on that chain.
    [Chain.BNB_SMART_CHAIN_MAINNET]: [
      Lender.AAVE_V3,
      AAVE_V4_PREFIX,
      Lender.VENUS,
    ],
    [Chain.XDC_NETWORK]: [Lender.PRIME_FI, Lender.FATHOM],
    [Chain.HYPEREVM]: [Lender.HYPERLEND, Lender.HYPURRFI],
  },
  lowPriorityLendersPerChain: {},
  excludedLendersPerChain: {},
  staleRejectSeconds: DEFAULT_STALE_REJECT_SECONDS,
  outlierGuard: DEFAULT_OUTLIER_GUARD,
}

/**
 * A candidate price with every ranking tier resolved.
 *
 * Ranking is LEXICOGRAPHIC, not a weighted score: each tier is compared in
 * order and only ties fall through to the next. That avoids having to
 * justify exchange rates between incomparable things ("is 2x the depth worth
 * three hours of staleness?"), and makes every decision explainable in one
 * sentence — which tier decided it.
 */
interface PriceCandidate {
  oracleKey: string
  priceUSD: number
  chainId: string
  lender: string
  family: string
  feedKey: string
  /** Entry identity — the last thing that can break a tie deterministically */
  marketUid: string
  /** Tier 1 — is this feed past its own learned cadence? */
  freshnessRank: number
  /** Tier 2 — does this feed habitually disagree with the market? */
  reliabilityRank: number
  /** Tier 3 — how the number was obtained (direct < quoted < derived) */
  derivationRank: number
  /** Tier 4 — operator preference from config */
  lenderRank: number
  /** Tier 5 — market depth behind the price, DESC (0 when unknown) */
  depth: number
  /** Tier 6 — configured chain preference */
  chainRank: number
}

/** Sentinel rank for lenders absent from the priority list. */
const LENDER_RANK_UNLISTED = 1000
/** Sentinel rank for lenders explicitly marked low priority. */
const LENDER_RANK_LOW = 2000
/** Sentinel rank for chains absent from the chain priority list. */
const CHAIN_RANK_UNLISTED = 1000

/**
 * Operator preference for a (lender, chain) pair. Returns -1 when the combo
 * is vetoed outright.
 *
 * This is now ONE tier among several rather than the whole ranking — it sits
 * below the evidence tiers (a configured favourite still loses to a fresher,
 * more direct price) and above depth.
 */
function lenderRankOf(
  lender: string,
  chainId: string,
  cfg: FlattenPriorityConfig,
): number {
  const excluded = cfg.excludedLenders[chainId]
  if (excluded?.some((p) => lender.startsWith(p))) return -1
  const excludedPerChain = cfg.excludedLendersPerChain[chainId]
  if (excludedPerChain?.some((p) => lender.startsWith(p))) return -1

  const lowPerChain = cfg.lowPriorityLendersPerChain[chainId]
  const isLow = lowPerChain
    ? lowPerChain.some((p) => lender.startsWith(p))
    : cfg.lowPriorityLenders.some((p) => lender.startsWith(p))
  if (isLow) return LENDER_RANK_LOW

  const lenderPriority =
    cfg.lenderPriorityPerChain[chainId] ?? cfg.lenderPriority
  const idx = lenderPriority.findIndex((p) => lender.startsWith(p))
  return idx >= 0 ? idx : LENDER_RANK_UNLISTED
}

/** Depth for a candidate, accepting both the per-market and legacy shapes. */
function depthOf(
  entryMarketUid: string | undefined,
  lender: string,
  depth: DepthMap | TvlMap | undefined,
): number {
  if (!depth) return 0
  if (entryMarketUid) {
    const perMarket = depth[entryMarketUid]
    if (typeof perMarket === 'number') return perMarket
  }
  const perLender = depth[lender]
  return typeof perLender === 'number' ? perLender : 0
}

/**
 * Compare two candidates tier by tier. Negative when `a` is better.
 *
 * Measured evidence about THIS feed (is it still updating, does it track the
 * market) outranks the structural prior about HOW the price was computed: a
 * direct read from a feed proven to sit 27% off the market is worse than a
 * derived price that tracks it. Both only differentiate on NEGATIVE
 * evidence — `unknown` ranks equal to `fresh`/`good` — so a feed with no
 * history never loses to one merely because the other has been watched.
 *
 * The final tiebreak is on identity, not on input order, so the same
 * structured prices always flatten to the same map regardless of how the
 * chains and lenders happened to be enumerated. Downstream USD values must
 * not change just because a fetcher returned in a different order.
 */
function compareCandidates(a: PriceCandidate, b: PriceCandidate): number {
  return (
    a.freshnessRank - b.freshnessRank ||
    a.reliabilityRank - b.reliabilityRank ||
    a.derivationRank - b.derivationRank ||
    a.lenderRank - b.lenderRank ||
    b.depth - a.depth ||
    a.chainRank - b.chainRank ||
    cmpStr(a.lender, b.lender) ||
    cmpStr(a.chainId, b.chainId) ||
    // One lender can emit many entries for the same asset (Euler prices a
    // token in every vault that lists it), so lender+chain is NOT a unique
    // identity. Without these last two the winner among them fell out of
    // enumeration order and the flat price moved between runs.
    cmpStr(a.marketUid, b.marketUid) ||
    a.priceUSD - b.priceUSD
  )
}

function cmpStr(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0
}

/** Options for the evidence-driven tiers. */
export interface SelectPricesOptions {
  /**
   * Market depth behind each price, keyed by `marketUid`
   * (`LENDER:chainId:asset`) or, for the legacy shape, by lender.
   * Orders the long tail of lenders that no priority list mentions — without
   * it, ties there fall through to the deterministic identity tiebreak.
   */
  depth?: DepthMap | TvlMap
  /** Learned per-feed cadence + agreement stats from `feedStats.ts` */
  feedStats?: FeedStatsMap
  /** Wall clock override (unix seconds), for deterministic tests */
  nowSeconds?: number
  /**
   * Receives the winning candidate per asset group. Provenance is otherwise
   * unrecoverable from the flat map — "why is AVAX $23.78" should not
   * require re-deriving the whole pipeline.
   */
  onSelection?: (selection: PriceSelection) => void
}

/**
 * Flattens structured oracle prices to a simple USD price map.
 *
 * Ranking, in order:
 *   1. freshness   — past this feed's own learned cadence loses
 *   2. reliability — a feed that habitually disagrees with the market loses
 *   3. derivation  — a direct oracle read beats a price derived from one
 *   4. lender      — operator preference from the priority config
 *   5. depth       — deeper market wins (also picks an asset's home chain)
 *   6. chain       — configured chain preference
 *   7. identity    — deterministic, never input order
 *
 * Before ranking, candidates contradicting a strong cross-feed consensus are
 * dropped by the outlier guard.
 */
export function selectAssetGroupPrices(
  structuredPrices: StructuredOraclePrices,
  lists: TokenListInput = {},
  depthOrTvl: DepthMap | TvlMap = {},
  priorityCfg: FlattenPriorityConfig = DEFAULT_PRIORITY,
  options: SelectPricesOptions = {},
): USDPriceMap {
  const staleRejectSeconds =
    priorityCfg.staleRejectSeconds ?? DEFAULT_STALE_REJECT_SECONDS
  const guard =
    priorityCfg.outlierGuard === undefined
      ? DEFAULT_OUTLIER_GUARD
      : priorityCfg.outlierGuard
  const depth = options.depth ?? depthOrTvl
  const feedStats = options.feedStats
  const nowSeconds = options.nowSeconds ?? Math.floor(Date.now() / 1000)

  // 1. Collect candidates, bucketed per assetGroup
  const byKey = new Map<string, PriceCandidate[]>()

  for (const [chainId, lenders] of Object.entries(structuredPrices)) {
    const tokenList = lists[chainId] ?? {}

    for (const [lender, entries] of Object.entries(lenders)) {
      if (!Array.isArray(entries)) continue // skip malformed data
      const lenderRank = lenderRankOf(lender, chainId, priorityCfg)
      if (lenderRank < 0) continue // vetoed lender+chain combo
      const family = lenderFamily(lender)
      const chainIdx = priorityCfg.chainPriority.indexOf(chainId)
      const chainRank = chainIdx >= 0 ? chainIdx : CHAIN_RANK_UNLISTED

      for (const entry of entries) {
        if (!entry?.asset || entry.priceUSD <= 0) continue

        const feedKey = feedKeyOf(entry, lender)
        const stat = feedStats?.[feedStatKey(chainId, feedKey)]

        const freshness =
          staleRejectSeconds > 0
            ? classifyFreshness(
                entry.updatedAt,
                stat,
                staleRejectSeconds,
                nowSeconds,
              )
            : 'unknown'

        const group = tokenList[entry.asset]?.assetGroup
        // A placeholder group is junk, not an identity — exclude the entry
        // outright. Re-keying it to `<chainId>-<address>` would still collide
        // with every OTHER placeholder-group token. A genuinely missing group
        // (native gas tokens without a curated group) still falls through to
        // the address key below.
        if (group != null && isPlaceholderGroup(group)) continue

        const oracleKey = group ?? `${chainId}-${entry.asset.toLowerCase()}`

        const candidate: PriceCandidate = {
          oracleKey,
          priceUSD: entry.priceUSD,
          chainId,
          lender,
          family,
          feedKey,
          marketUid: entry.marketUid ?? '',
          derivationRank: DERIVATION_RANK[resolveDerivation(entry)],
          freshnessRank: FRESHNESS_RANK[freshness],
          reliabilityRank: RELIABILITY_RANK[classifyReliability(stat)],
          lenderRank,
          depth: depthOf(entry.marketUid, lender, depth),
          chainRank,
        }

        const bucket = byKey.get(oracleKey)
        if (bucket) bucket.push(candidate)
        else byKey.set(oracleKey, [candidate])
      }
    }
  }

  // 2. Per assetGroup: drop consensus outliers, then take the best candidate
  const flatPrices: USDPriceMap = {}
  for (const [oracleKey, candidates] of byKey) {
    const viable = guard ? rejectOutliers(candidates, guard) : candidates
    let best = viable[0]
    for (const c of viable) {
      if (compareCandidates(c, best) < 0) best = c
    }
    if (!best) continue
    flatPrices[oracleKey] = best.priceUSD

    if (options.onSelection) {
      options.onSelection({
        assetGroup: oracleKey,
        priceUSD: best.priceUSD,
        chainId: best.chainId,
        lender: best.lender,
        feedKey: best.feedKey,
        derivation: derivationOfRank(best.derivationRank),
        candidates: candidates.length,
        rejected: candidates.length - viable.length,
      })
    }
  }

  return flatPrices
}

const RANK_TO_DERIVATION: PriceDerivation[] = ['direct', 'quoted', 'derived']
function derivationOfRank(rank: number): PriceDerivation {
  return RANK_TO_DERIVATION[rank] ?? 'derived'
}
